import { http } from './http';
import { checkApiHealth } from '../utils/apiHealth';
import i18n from '../i18n';

export interface MenuItemData {
  id: number;
  label: string;
  url: string | null;
  icon: string | null;
  target: string;
  children?: MenuItemData[];
}

export interface PageData {
  id: number;
  slug: string;
  title: string;
  description: string | null;
  content?: string | null;
  meta_title?: string;
  meta_description?: string;
  meta_keywords?: string;
}

export interface SiteInfo {
  app_name: string;
  supported_networks: string[];
  supported_languages: string[];
}

export interface AppData {
  id: number;
  name: string;
  slug: string;
  description?: string | null;
  type: 'extension' | 'android' | 'ios' | 'both';
  version?: string | null;
  download_count: number;
  category?: { id: number; name: string; slug: string } | null;
  icon_url?: string;
  /** True when a ZIP (extension) file is stored on the API. */
  has_file?: boolean;
  download_url?: string;
  android_url?: string;
  ios_url?: string;
}

export interface AppCategoryData {
  id: number;
  name: string;
  slug: string;
  description?: string | null;
  icon?: string | null;
  apps: AppData[];
}

export interface ArticleCategoryData {
  id: number;
  name: string;
  slug: string;
  description?: string | null;
  meta_title?: string | null;
  meta_description?: string | null;
  articles_count?: number;
}

export interface ArticleTagRef {
  name: string;
  slug: string;
}

export interface ArticleTagData {
  name: string;
  slug: string;
  articles_count: number;
}

export interface SeoPageData {
  slug: string;
  type?: string;
  page_type?: string;
  entity_type?: string;
  title: string;
  h1: string;
  content: string;
  meta_title?: string | null;
  meta_description?: string | null;
  canonical_url?: string | null;
  public_path?: string;
  language: string;
  status: 'draft' | 'published' | 'noindex';
  word_count: number;
  generated_at?: string | null;
  updated_at?: string | null;
}

export interface ArticleData {
  id: number;
  slug: string;
  title: string;
  excerpt?: string | null;
  content?: string | null;
  tags?: ArticleTagRef[];
  published_at?: string | null;
  is_featured?: boolean;
  view_count?: number;
  featured_image?: string | null;
  featured_image_url?: string | null;
  og_image?: string | null;
  og_image_url?: string | null;
  meta_title?: string | null;
  meta_description?: string | null;
  meta_keywords?: string | null;
  category?: { id: number; name: string; slug: string } | null;
  author?: { name: string } | null;
}

export interface SiteContactLinkData {
  id: number;
  category: 'social' | 'email' | 'phone' | 'other';
  platform?: string | null;
  label: string;
  value: string;
  href: string;
  icon?: string | null;
  sort_order: number;
}

export interface ArticlesResponse {
  data: ArticleData[];
  meta?: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    from?: number;
    to?: number;
  };
}

const SEO_LANGS = new Set(['ar', 'zh', 'ko']);

async function fetchSeoPageBySlug(slug: string, language: string): Promise<SeoPageData | null> {
  try {
    const { data } = await http.get(`/public/seo-pages/${encodeURIComponent(slug)}`, {
      headers: { 'Accept-Language': language },
    });
    if (data?.success && data?.data) {
      return data.data;
    }
  } catch {
    /* fall through to resolve */
  }

  try {
    const path = language === 'en' ? `page/${slug}` : `${language}/page/${slug}`;
    const params = new URLSearchParams({ path });
    const { data } = await http.get(`/public/seo-pages/resolve?${params.toString()}`, {
      headers: { 'Accept-Language': language },
    });
    return data?.success && data?.data ? data.data : null;
  } catch {
    return null;
  }
}

function parseSeoPath(path: string, fallbackLang: string): { slug: string; language: string } {
  const parts = path.replace(/^\//, '').split('/').filter(Boolean);
  if (parts[0] === 'page') {
    return { language: 'en', slug: parts.slice(1).join('/') };
  }
  if (parts.length >= 2 && SEO_LANGS.has(parts[0]) && parts[1] === 'page') {
    return { language: parts[0], slug: parts.slice(2).join('/') };
  }

  return { slug: parts.join('/'), language: fallbackLang };
}

export const publicApi = {
  getMenu: async (location: 'header' | 'footer' | 'sidebar'): Promise<MenuItemData[]> => {
    try {
      const { data } = await http.get(`/public/menu/${location}`, {
        headers: { 'Accept-Language': i18n.language },
      });
      return Array.isArray(data?.data) ? data.data : [];
    } catch {
      return [];
    }
  },

  getPages: async (): Promise<PageData[]> => {
    const { data } = await http.get('/public/pages', {
      headers: { 'Accept-Language': i18n.language },
    });
    return Array.isArray(data?.data) ? data.data : [];
  },

  getPage: async (slug: string): Promise<PageData | null> => {
    try {
      const { data } = await http.get(`/public/pages/${encodeURIComponent(slug)}`, {
        headers: { 'Accept-Language': i18n.language },
      });
      const page = data?.data;
      return page?.slug ? page : null;
    } catch {
      return null;
    }
  },

  getCaptchaStatus: async (action: 'login' | 'register' | 'forgot_password' | '2fa' | 'contact' = 'login'): Promise<{
    requires_captcha: boolean;
    sitekey: string;
    sitekey_tail?: string;
  }> => {
    const { data } = await http.get(`/captcha-status?action=${action}`);
    return data ?? { requires_captcha: false, sitekey: '' };
  },

  getSiteInfo: async (): Promise<SiteInfo & { site_stats?: { years_online: number; total_users: number; annual_transactions: number; total_transactions: number } | null }> => {
    try {
      const { data } = await http.get('/public/site-info');
      return data?.data ?? { app_name: '', supported_networks: [], supported_languages: [] };
    } catch {
      return { app_name: '', supported_networks: [], supported_languages: [] };
    }
  },

  getSiteContacts: async (): Promise<SiteContactLinkData[]> => {
    try {
      const { data } = await http.get('/public/site-contacts');
      return Array.isArray(data?.data) ? data.data : [];
    } catch {
      return [];
    }
  },

  submitContact: async (payload: { name: string; email: string; subject: string; message: string; 'cf-turnstile-response'?: string }): Promise<{ message: string }> => {
    const { data } = await http.post('/contact', payload);
    return data ?? { message: 'Message sent successfully.' };
  },

  getApps: async (): Promise<AppCategoryData[]> => {
    const { data } = await http.get('/public/apps');
    return Array.isArray(data?.data) ? data.data : [];
  },

  getAppBySlug: async (slug: string): Promise<AppData | null> => {
    try {
      const { data } = await http.get(`/public/apps/slug/${encodeURIComponent(slug)}`);
      return data?.data ?? null;
    } catch {
      return null;
    }
  },

  getAppDownloadUrl: async (id: number, platform?: 'android' | 'ios'): Promise<string | { android_url?: string; ios_url?: string }> => {
    const q = platform ? `?platform=${platform}` : '';
    const { data } = await http.get(`/public/apps/${id}/download${q}`);
    const d = data?.data;
    if (d?.url) return d.url;
    if (d?.android_url || d?.ios_url) return { android_url: d.android_url, ios_url: d.ios_url };
    return '';
  },

  /** @deprecated Homepage no longer displays stats; returns null when unavailable. */
  getPublicStats: async (): Promise<{ years_online: number; total_users: number; annual_transactions: number; total_transactions: number } | null> => {
    const apiUp = await checkApiHealth();
    if (!apiUp) {
      return null;
    }
    try {
      const siteInfo = await publicApi.getSiteInfo();
      const stats = siteInfo?.site_stats;
      if (stats) {
        return stats;
      }
    } catch {
      // Ignore
    }
    return null;
  },

  getArticleCategories: async (): Promise<ArticleCategoryData[]> => {
    const { data } = await http.get('/public/articles/categories', {
      headers: { 'Accept-Language': i18n.language },
    });
    return Array.isArray(data?.data) ? data.data : [];
  },

  getArticleCategory: async (slug: string): Promise<ArticleCategoryData | null> => {
    const { data } = await http.get(`/public/articles/categories/${slug}`, {
      headers: { 'Accept-Language': i18n.language },
    });
    return data?.data ?? null;
  },

  getArticles: async (opts?: { category?: string; featured?: boolean; tag?: string; limit?: number; page?: number }): Promise<ArticlesResponse> => {
    const params = new URLSearchParams();
    if (opts?.category) params.set('category', opts.category);
    if (opts?.featured) params.set('featured', '1');
    if (opts?.tag) params.set('tag', opts.tag);
    if (opts?.limit) params.set('limit', String(opts.limit));
    if (opts?.page) params.set('page', String(opts.page));
    const q = params.toString() ? `?${params.toString()}` : '';
    const { data } = await http.get(`/public/articles${q}`, {
      headers: { 'Accept-Language': i18n.language },
    });
    return {
      data: Array.isArray(data?.data) ? data.data : [],
      meta: data?.meta,
    };
  },

  getArticle: async (slug: string): Promise<ArticleData> => {
    const { data } = await http.get(`/public/articles/${encodeURIComponent(slug)}`, {
      headers: { 'Accept-Language': i18n.language },
    });
    return data?.data ?? ({} as ArticleData);
  },

  getArticleTags: async (): Promise<ArticleTagData[]> => {
    const { data } = await http.get('/public/articles/tags', {
      headers: { 'Accept-Language': i18n.language },
    });
    return Array.isArray(data?.data) ? data.data : [];
  },

  getArticleTag: async (slug: string): Promise<ArticleTagData | null> => {
    try {
      const { data } = await http.get(`/public/articles/tags/${encodeURIComponent(slug)}`, {
        headers: { 'Accept-Language': i18n.language },
      });
      return data?.data ?? null;
    } catch {
      return null;
    }
  },

  /** Fetch programmatic SEO page by slug (preferred — no query-string proxy issues). */
  getSeoPage: async (slug: string, lang?: string): Promise<SeoPageData | null> => {
    return fetchSeoPageBySlug(slug, lang || i18n.language);
  },

  /** @deprecated use getSeoPage(slug, lang) */
  resolveSeoPage: async (path: string, lang?: string): Promise<SeoPageData | null> => {
    const { slug, language } = parseSeoPath(path, lang || i18n.language);
    return fetchSeoPageBySlug(slug, language);
  },
};
